import styles from "./StockInfo.module.scss";
import { FC, useRef, useState } from "react";
import { Stock } from "@/models/stock";
import { classNames } from "@/lib/utils";
import Link from "next/link";
import { useAtomValue } from "jotai";
import { greyState, textColorState, ortexColorState, backgroundColorState, darkerBackgroundColorState, themeState } from "@/lib/store";
import { Box, IconButton, Typography, useMediaQuery } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const StockChainSelector: FC<{ stock: Stock; isCustomView?: boolean }> = ({ stock, isCustomView = false }) => {
    const grey = useAtomValue(greyState);
    const textColor = useAtomValue(textColorState);
    const theme = useAtomValue(themeState);
    const ortexColor = useAtomValue(ortexColorState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);

    const is4k = useMediaQuery("(min-width: 1680px)");
    const isSmallDesktop = useMediaQuery("(min-width: 1000px) and (max-width:1280px)");
    const isNotMobile = useMediaQuery("(min-width: 768px)");
    const dateSelectorRef = useRef<HTMLDivElement>(null);

    const [datesScrollEnd, setDatesScrollEnd] = useState(false);
    const [datesScrollStart, setDatesScrollStart] = useState(true);
    const [scrollStep, setScrollStep] = useState<number>(1);

    const handleScroll = () => {
        const scrollWidth = dateSelectorRef.current.scrollWidth;
        const width = dateSelectorRef.current.clientWidth;
        const scrollLeft = dateSelectorRef.current.scrollLeft;

        if (scrollLeft > 0) setDatesScrollStart(false);
        else setDatesScrollStart(true);

        if (scrollWidth - width <= scrollLeft) setDatesScrollEnd(true);
        else setDatesScrollEnd(false);
    };

    const scrollTo = (side: "left" | "right") => {
        dateSelectorRef.current.scroll({
            left: side === "left" ? 0 : isNotMobile ? dateSelectorRef.current.scrollWidth : (dateSelectorRef.current.scrollWidth / 3) * scrollStep,
            top: 0,
            behavior: "smooth",
        });
        if (!isNotMobile) {
            side === "left" ? setScrollStep(1) : setScrollStep((prev) => prev + 1);
        }
    };

    return (
        <Box className={styles["chain-date-selector"]} sx={{ gridArea: "c", height: "100%", ...(isCustomView && { maxHeight: "50px" }) }}>
            {stock.other_listings && stock.other_listings.length > 7 && !isSmallDesktop && !is4k && (
                <IconButton
                    onClick={() => scrollTo("left")}
                    aria-label="Earlier expiry dates"
                    size="small"
                    sx={{
                        backgroundColor,
                        color: textColor,
                        borderRadius: 0,
                        padding: 0,
                        height: "100%",
                        marginBottom: 0,
                        border: 0,
                        borderRight: "4px solid",
                        borderColor: theme === "dark" ? darkerBackgroundColor : "#fff",
                        ":hover": { backgroundColor },
                    }}
                    className={classNames(styles.arrow, styles.left, styles[datesScrollStart && "hidden"])}
                >
                    <KeyboardArrowLeftIcon sx={{ fontSize: "18px" }} />
                </IconButton>
            )}
            <Box
                className={classNames(!datesScrollEnd && styles.scrolleableLeft, !datesScrollStart && styles.scrolleableRight)}
                onScroll={handleScroll}
                ref={dateSelectorRef}
                sx={{ height: "100%", width: !datesScrollEnd ? "calc(100% - 18px)" : "100%" }}
            >
                <Box id="flexing" sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginX: "auto", overflowX: "auto", height: "100%", overflowY: "hidden" }}>
                    {stock.other_listings &&
                        stock.other_listings?.map((value, index) => (
                            <Box key={value.id} sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "text.secondary",
                                        height: "100%",
                                        textAlign: "center",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    key={value.exchange}
                                >
                                    {value.exchange}:
                                </Typography>

                                <Typography
                                    variant="overline"
                                    key={value.id}
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        "> a": {
                                            textDecoration: "none",
                                            color: textColor,
                                            paddingLeft: "2.5px",
                                            borderRadius: "2.5px",
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                                color: ortexColor,
                                                backgroundColor: "rgb(49, 171, 166,0.1)",
                                            },
                                        },
                                    }}
                                >
                                    <Link legacyBehavior href={`/s/${value.exchange}/${value.ticker}`}>{`${value.ticker}`}</Link>
                                    {index !== stock.other_listings.length - 1 && <span style={{ marginLeft: "4px", marginRight: "4px", color: grey }}>{"|"}</span>}
                                </Typography>
                            </Box>
                        ))}
                </Box>
            </Box>
            {stock.other_listings && stock.other_listings.length > 7 && !isSmallDesktop && !is4k && (
                <IconButton
                    onClick={() => scrollTo("right")}
                    aria-label="Later expiry dates"
                    size="small"
                    sx={{ backgroundColor, color: textColor, borderRadius: 0, padding: 0, height: "100%", border: 0, borderLeft: "4px solid", borderColor: theme === "dark" ? darkerBackgroundColor : "#fff", ":hover": { backgroundColor } }}
                    className={classNames(styles.arrow, styles.right, styles[datesScrollEnd && "hidden"])}
                >
                    <KeyboardArrowRightIcon sx={{ fontSize: "18px" }} />
                </IconButton>
            )}
        </Box>
    );
};

export default StockChainSelector;
